import { identifyApiErrorCode } from '@/utils/axios';
import { EventProperties } from '@calm/evexia-analytics';
import { CustomAxiosError } from '@calm/evexia-web-sdk';
import { UnderageErrorCode } from '@rhg/constants';
import { useLogoutConfirmation } from '@rhg/hooks/use-logout';
import {
    RegistrationFlow,
    RegistrationFlowContextState,
} from '@rhg/hooks/use-registration-context/types';
import { logger } from '@rhg/libs/logger';
import { isUpgradeCountryError } from '@rhg/utils/eligibility';
import { identifyInvitationErrorType } from '@rhg/utils/invitations';
import { UnderageDialog } from '../../../age-and-gender/underage-dialog';
import { CountryErrorDialog } from '../../country-error-dialog';
import { GeneralErrorDialog } from '../../general-error-dialog';
import { InvitationErrorDialog } from '../../invitation-error-dialog';

export function RegistrationContainerErrorDialog({
    registrationFlow,
    newSponsor,
    forcedError,
    setForcedError,
    createCircleReset,
    createCircleError,
    submitFlowReset,
    submitFlowError,
}: Pick<
    RegistrationFlowContextState,
    | 'registrationFlow'
    | 'newSponsor'
    | 'submitFlowReset'
    | 'submitFlowError'
    | 'createCircleReset'
    | 'createCircleError'
    | 'forcedError'
    | 'setForcedError'
>) {
    const handleClose = () => {
        createCircleReset();
        submitFlowReset();
        setForcedError();
    };

    const { openLogoutDialog } = useLogoutConfirmation();
    const onLogout = () => {
        handleClose();
        openLogoutDialog().catch((err: Error) =>
            logger.error('Error opening logout dialog', {}, err)
        );
    };

    const isNewRegistration =
        registrationFlow === RegistrationFlow.NewRegistration ||
        registrationFlow === RegistrationFlow.NewInviteeRegistration;

    const circleInvitationError = identifyInvitationErrorType(
        createCircleError as CustomAxiosError
    );
    const submitInvitationError = identifyInvitationErrorType(
        submitFlowError as CustomAxiosError
    );

    const trackProps: EventProperties = {
        ...(newSponsor ? { PartnerID: newSponsor?.id } : {}),
        IsDependent: [
            RegistrationFlow.NewInviteeRegistration,
            RegistrationFlow.ChangeOfInviteeSponsorship,
            RegistrationFlow.ExistingInviteeRegistration,
        ].includes(registrationFlow),
    };

    if (
        [circleInvitationError, submitInvitationError, forcedError].includes(
            'claimed'
        )
    ) {
        return (
            <InvitationErrorDialog
                type="claimed"
                onClose={handleClose}
                trackProps={trackProps}
            />
        );
    }

    if (
        [circleInvitationError, submitInvitationError, forcedError].includes(
            'expired'
        )
    ) {
        return (
            <InvitationErrorDialog
                type="expired"
                onClose={handleClose}
                trackProps={trackProps}
            />
        );
    }

    const hasInvitationCountryError = [
        circleInvitationError,
        submitInvitationError,
        forcedError,
    ].includes('country');
    const hasUpgradeCountryError = isUpgradeCountryError(
        submitFlowError as CustomAxiosError
    );
    if (hasInvitationCountryError || hasUpgradeCountryError) {
        return (
            <CountryErrorDialog
                source={hasInvitationCountryError ? 'invitation' : 'upgrade'}
                onClose={handleClose}
                trackProps={trackProps}
            />
        );
    }

    const createCircleErrorCode = identifyApiErrorCode(
        createCircleError as CustomAxiosError
    );
    const submitFlowErrorCode = identifyApiErrorCode(
        submitFlowError as CustomAxiosError
    );
    if (
        createCircleErrorCode?.includes(UnderageErrorCode) ||
        submitFlowErrorCode?.includes(UnderageErrorCode)
    ) {
        return (
            <UnderageDialog
                onClose={handleClose}
                trackProps={trackProps}
                onLogout={onLogout}
            />
        );
    }

    // Generic Error
    return (
        <GeneralErrorDialog
            onClose={handleClose}
            showSupportEmail={isNewRegistration}
            showSupportMessage={isNewRegistration}
        />
    );
}
